import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../../../components/common/Toast/toast";
import { getReport } from "../../../../../services/ClassroomManagementService.js";
import NewCustomDataTable from "../../../../components/common/NewCustomDataTable.jsx";
import StatTile from "../../components/StatTile.jsx";
import ReportBreakdownModal, { BREAKDOWN_VIEW, utilTone } from "./ReportBreakdownModal.jsx";
import { useWaypoint } from "../../../../components/common/waypoint/WaypointProvider.jsx";
import { BarsChart, StatusDonut, TrendChart, SESSION_OUTCOME_TONE } from "../../components/ClassroomCharts.jsx";
/* The page kit, in place of the classroom app's own cls-* vocabulary --
   see LabsTab for the full note. */
import "../../../../components/Trainer/TrainerDashboard.css";
import "../../../../components/common/lms-pages.css";

const toDateStr = (d) => d.toISOString().slice(0, 10);

const RANGES = [
  {
    key: "thisWeek",
    label: "This week",
    compute: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { from: toDateStr(start), to: toDateStr(end) };
    },
  },
  {
    key: "lastWeek",
    label: "Last week",
    compute: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() - 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { from: toDateStr(start), to: toDateStr(end) };
    },
  },
  {
    key: "thisMonth",
    label: "This month",
    compute: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: toDateStr(start), to: toDateStr(end) };
    },
  },
  {
    key: "lastMonth",
    label: "Last month",
    compute: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: toDateStr(start), to: toDateStr(end) };
    },
  },
];

const VIEWS = [
  { key: "trainers", label: "Trainers" },
  { key: "course", label: "By course" },
  { key: "labs", label: "Labs" },
];

const DASH = "—";

/* The utilization figure carries the tone; there is no meter beside it.
   Sixty small bars down a column read as noise -- the same call
   lms-pages.css makes for the attendance figure, in the same three tones.
   utilTone lives in ReportBreakdownModal, whose Labs breakdown colours the
   same figure, so the two cannot drift. */

const downloadCsv = (filename, rows) => {
  const csv = rows.map((r) => r.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ReportsTab = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const [rangeKey, setRangeKey] = useState("thisMonth");
  const [from, setFrom] = useState(() => RANGES.find((r) => r.key === "thisMonth").compute().from);
  const [to, setTo] = useState(() => RANGES.find((r) => r.key === "thisMonth").compute().to);
  const [view, setView] = useState("trainers");
  // The Labs view's Status filter; the API narrows the table by it.
  const [labStatus, setLabStatus] = useState("");

  const load = useCallback(async () => {
    if (!from || !to || from > to) {
      setReport(null);
      return;
    }
    try {
      setLoading(true);
      const data = await getReport(from, to, view, view === "labs" ? labStatus : "");
      setReport(data);
    } catch (error) {
      console.error("Error loading reports data:", error);
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  }, [from, to, view, labStatus]);

  useEffect(() => { load(); }, [load]);

  // ── The figures open what they total ──────────────────────────────
  // A figure is a total, not a record, so it opens ReportBreakdownModal
  // -- the rows behind it -- and every trainer, course and lab in that
  // list opens through Waypoint (instruction_ui.json, "waypoint").
  const { open: openEntity } = useWaypoint();
  const [breakdown, setBreakdown] = useState(null);
  const breakdownRequestRef = useRef(0);

  const openBreakdown = useCallback(async (kind, label) => {
    if (!report) return;
    const requestId = ++breakdownRequestRef.current;
    const needed = BREAKDOWN_VIEW[kind];
    const base = { kind, label, from, to, tiles: report.tiles };

    // The grouping on screen is the one the breakdown needs: no request.
    if (needed === view) {
      setBreakdown({ ...base, rows: report.rows, loading: false, error: null });
      return;
    }

    // The endpoint returns one grouping at a time, so another one is
    // fetched for the same period. The totals are the same either way.
    setBreakdown({ ...base, rows: [], loading: true, error: null });
    try {
      const data = await getReport(from, to, needed);
      if (breakdownRequestRef.current !== requestId) return;
      setBreakdown({ ...base, tiles: data?.tiles || report.tiles, rows: data?.rows || [], loading: false, error: null });
    } catch (error) {
      if (breakdownRequestRef.current !== requestId) return;
      console.error("Error loading the rows behind a report figure:", error);
      setBreakdown({ ...base, rows: [], loading: false, error: "Could not load this breakdown. Close it and try again." });
    }
  }, [report, view, from, to]);

  const closeBreakdown = useCallback(() => {
    // A breakdown still loading must not reopen the popup once it lands.
    breakdownRequestRef.current += 1;
    setBreakdown(null);
  }, []);

  // A page opening a Waypoint from inside its own modal closes that modal
  // first; Waypoint's own back link is what returns the reader.
  const bridgeFromBreakdown = useCallback((type, id) => {
    if (!id) return;
    closeBreakdown();
    openEntity(type, id);
  }, [closeBreakdown, openEntity]);

  // A new period makes an open breakdown describe dates no longer shown.
  useEffect(() => {
    closeBreakdown();
  }, [from, to, closeBreakdown]);

  const applyRange = (key) => {
    setRangeKey(key);
    const r = RANGES.find((x) => x.key === key);
    if (r) {
      const { from: f, to: t } = r.compute();
      setFrom(f);
      setTo(t);
    }
  };

  const exportCsv = () => {
    if (!report) return;
    let rows = [];
    if (view === "trainers") {
      rows = [["Trainer", "Specialization", "Batches", "Sessions", "Teaching hours", "Days taught", "Leave days", "Cancelled"]];
      report.rows.forEach((r) => {
        rows.push([r.trainerName, r.specialization || "", r.batchesCount, r.sessions, r.hours.toFixed(1), r.daysTaught, r.leaveDays, r.cancelled]);
      });
    } else if (view === "course") {
      rows = [["Course", "Batches", "Sessions", "Teaching hours"]];
      report.rows.forEach((r) => {
        rows.push([r.courseName, r.batchesCount, r.sessions, r.hours.toFixed(1)]);
      });
    } else {
      rows = [["Lab", "Sessions", "Teaching hours", "Utilization %", "Shifted-in", "Status"]];
      report.rows.forEach((r) => {
        rows.push([r.classroomNo || "—", r.sessions, r.hours.toFixed(1), r.utilization.toFixed(1), r.shiftedIn, r.isActive === false ? "Out of order" : "Active"]);
      });
    }
    downloadCsv(`classroom-report-${view}-${from}-to-${to}.csv`, rows);
  };

  /* One table, three shapes. The headers are picked by view rather than
     three tables written out in full, which is what had the old file
     repeating the same thead/tbody/empty-row three times over. */
  const headers = useMemo(() => {
    if (view === "trainers") {
      return [
        {
          title: "Trainer",
          prop: "trainerName",
          isSortable: true,
          cell: (row) => (
            <div className="lp-cell">
              <span className="lp-name">{row.trainerName}</span>
              {row.specialization && <span className="lp-sub">{row.specialization}</span>}
            </div>
          ),
        },
        { title: "Batches", prop: "batchesCount", isSortable: true, cell: (r) => <span>{r.batchesCount}</span> },
        { title: "Sessions", prop: "sessions", isSortable: true, cell: (r) => <span>{r.sessions}</span> },
        {
          title: "Hours",
          prop: "hours",
          isSortable: true,
          cell: (r) => <span className="srp-att">{r.hours.toFixed(1)}</span>,
        },
        { title: "Days taught", prop: "daysTaught", isSortable: true, cell: (r) => <span>{r.daysTaught}</span> },
        { title: "Leave", prop: "leaveDays", isSortable: true, cell: (r) => <span>{r.leaveDays}</span> },
        { title: "Cancelled", prop: "cancelled", isSortable: true, cell: (r) => <span>{r.cancelled}</span> },
      ];
    }

    if (view === "course") {
      return [
        {
          title: "Course",
          prop: "courseName",
          isSortable: true,
          cell: (r) => <span className="pg-strong">{r.courseName}</span>,
        },
        { title: "Batches", prop: "batchesCount", isSortable: true, cell: (r) => <span>{r.batchesCount}</span> },
        { title: "Sessions", prop: "sessions", isSortable: true, cell: (r) => <span>{r.sessions}</span> },
        {
          title: "Hours",
          prop: "hours",
          isSortable: true,
          cell: (r) => <span className="srp-att">{r.hours.toFixed(1)}</span>,
        },
      ];
    }

    return [
      {
        title: "Lab",
        prop: "classroomNo",
        isSortable: true,
        cell: (r) => <span className="pg-strong">{r.classroomNo || DASH}</span>,
      },
      { title: "Sessions", prop: "sessions", isSortable: true, cell: (r) => <span>{r.sessions}</span> },
      {
        title: "Hours",
        prop: "hours",
        isSortable: true,
        cell: (r) => <span className="srp-att">{r.hours.toFixed(1)}</span>,
      },
      {
        title: "Utilization",
        prop: "utilization",
        isSortable: true,
        cell: (r) => (
          <span className={`srp-att${utilTone(r.utilization)}`}>
            {r.utilization.toFixed(0)}%
          </span>
        ),
      },
      { title: "Shifted in", prop: "shiftedIn", isSortable: true, cell: (r) => <span>{r.shiftedIn}</span> },
      {
        title: "Status",
        prop: "isActive",
        isSortable: true,
        cell: (r) =>
          r.isActive === false ? (
            <span className="pg-pill pg-pill--bad">Out of order</span>
          ) : (
            <span className="pg-pill pg-pill--good">Active</span>
          ),
      },
    ];
  }, [view]);

  /* What a row of the table IS, in the grouping on screen: a trainer, a
     course or a lab, each one Waypoint knows how to show. A lab row with
     no classroomId -- held sessions whose batch has no room set -- is a
     bucket, not a record, so it opens nothing. */
  const rowTarget = (row) => {
    if (view === "trainers") return row?.trainerId ? ["trainer", row.trainerId, row.trainerName] : null;
    if (view === "course") return row?.courseId ? ["course", row.courseId, row.courseName] : null;
    return row?.classroomId ? ["classroom", row.classroomId, row.classroomNo] : null;
  };

  const openRow = (row) => {
    const target = rowTarget(row);
    if (target) openEntity(target[0], target[1]);
  };

  // ── The charts ────────────────────────────────────────────────────
  // Their series arrive worked out (classroommanagement.service.js
  // reports -> charts); this only writes the words each tooltip shows.
  const charts = report?.charts || null;
  const sessionWord = (n) => `${n} ${n === 1 ? "session" : "sessions"}`;

  const trendPoints = useMemo(
    () =>
      (charts?.trend?.points || []).map((p) => ({
        label: p.label,
        value: p.hours,
        tip: [` ${p.hours} teaching hours`, ` ${sessionWord(p.sessions)}`],
      })),
    [charts]
  );

  const rankingBars = useMemo(
    () =>
      (charts?.ranking?.bars || []).map((b) => ({
        label: b.label,
        value: b.value,
        tip: [
          charts.ranking.unit === "%" ? ` ${b.value}% of a 12-hour day in use` : ` ${b.value} teaching hours`,
          ` ${sessionWord(b.sessions)}`,
        ],
      })),
    [charts]
  );

  const outcomeSegments = useMemo(
    () =>
      (charts?.outcomes?.segments || []).map((s) => ({
        label: s.label,
        value: s.value,
        color: SESSION_OUTCOME_TONE[s.key],
      })),
    [charts]
  );

  if (loading && !report) {
    return (
      <div className="pg">
        <div className="pg-state">
          <span className="pg-spinner" role="status" aria-hidden="true" />
          <p className="pg-state-text">Loading the report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pg">
      <header className="pg-head no-print">
        <div className="pg-head-main">
          <h1 className="pg-title">Reports</h1>
          <p className="pg-sub">
            What was taught between two dates &mdash; by trainer, by course, or by room.
          </p>
        </div>

        <div className="pg-head-actions">
          <button type="button" className="pg-btn" onClick={exportCsv} disabled={!report}>
            Export CSV
          </button>
          <button type="button" className="pg-btn" onClick={() => window.print()} disabled={!report}>
            Print
          </button>
        </div>
      </header>

      {/* The period. A preset sets both dates; changing either date by
          hand drops the preset, because it is no longer "this month". */}
      <section className="pg-filters no-print" aria-label="Report period">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="rep-range">Period</label>
            <select
              id="rep-range"
              className="pg-select"
              value={rangeKey}
              onChange={(e) => applyRange(e.target.value)}
            >
              {RANGES.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
              <option value="">Custom</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="rep-from">From</label>
            <input
              id="rep-from"
              type="date"
              className="pg-input"
              value={from}
              onChange={(e) => { setRangeKey(""); setFrom(e.target.value); }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="rep-to">To</label>
            <input
              id="rep-to"
              type="date"
              className="pg-input"
              value={to}
              onChange={(e) => { setRangeKey(""); setTo(e.target.value); }}
            />
          </div>

          {view === "labs" && (
            <div className="pg-field pg-field--tight">
              <label className="pg-label" htmlFor="rep-status">Status</label>
              <select
                id="rep-status"
                className="pg-select"
                value={labStatus}
                onChange={(e) => setLabStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="outoforder">Out of order</option>
              </select>
            </div>
          )}
        </div>
      </section>

      <div className="lp-tabs no-print" role="tablist" aria-label="Report views">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            className={`lp-tab${view === v.key ? " is-on" : ""}`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {!report ? (
        <div className="pg-state">
          <h3 className="pg-state-title">No period chosen</h3>
          <p className="pg-state-text">Pick a From date on or before the To date.</p>
        </div>
      ) : (
        <>
          {/* Each figure opens the trainers, courses or labs it totals,
              and every one of those opens through Waypoint. "Days in
              period" does not open: it counts calendar days, and there
              is no record behind a day. A zero stays a plain figure. */}
          <div className="br-stats">
            {[
              { kind: "sessions", num: report.tiles.sessionsHeld, label: "Sessions held", open: report.tiles.sessionsHeld > 0 },
              { kind: "hours", num: report.tiles.teachingHours.toFixed(1), label: "Teaching hours", open: report.tiles.teachingHours > 0 },
              { kind: "utilization", num: `${report.tiles.avgUtilization.toFixed(0)}%`, label: "Avg lab utilization", variant: "free", open: report.tiles.avgUtilization > 0 },
              { kind: null, num: report.tiles.daysInPeriod, label: "Days in period", open: false },
              { kind: "cancelled", num: report.tiles.cancelledCount, label: "Cancelled (one-day)", variant: "crit", open: report.tiles.cancelledCount > 0 },
              { kind: "shifted", num: report.tiles.shiftedCount, label: "Shifted sessions", variant: "warn", open: report.tiles.shiftedCount > 0 },
            ].map((tile) => (
              <StatTile
                key={tile.label}
                num={tile.num}
                label={tile.label}
                variant={tile.variant}
                onClick={tile.open ? () => openBreakdown(tile.kind, tile.label) : undefined}
              />
            ))}
          </div>

          {report.rows.length === 0 ? (
            <div className="pg-state">
              <h3 className="pg-state-title">Nothing in this period</h3>
              <p className="pg-state-text">
                No sessions were held between {from} and {to}.
              </p>
            </div>
          ) : (
            <>
              {/* The period at a glance: hours over time, where they went
                  (following the tab), and what became of every session. */}
              {charts && (
                <div className="br-charts">
                  <TrendChart
                    title={charts.trend.unit === "month" ? "Teaching hours by month" : "Teaching hours by day"}
                    sub={
                      charts.trend.peak
                        ? `Busiest ${charts.trend.unit}: ${charts.trend.peak.label}, ${charts.trend.peak.hours} hours across ${sessionWord(charts.trend.peak.sessions)}.`
                        : null
                    }
                    points={trendPoints}
                    unit=" h"
                    empty="No teaching hours in this period."
                  />
                  <BarsChart
                    className="br-chart--main"
                    horizontal
                    title={charts.ranking.title}
                    sub={
                      view === "labs"
                        ? "How much of a 12-hour day each lab was in use."
                        : view === "course"
                          ? "Which courses took the most teaching hours."
                          : "Who taught the most hours."
                    }
                    note={charts.ranking.note}
                    bars={rankingBars}
                    unit={charts.ranking.unit}
                    max={charts.ranking.max || undefined}
                    empty="Nothing was taught in this period."
                  />
                  <StatusDonut
                    className="br-chart--side"
                    title="How sessions went"
                    sub="Every session scheduled in the period."
                    segments={outcomeSegments}
                    centreValue={charts.outcomes.total}
                    centreLabel="sessions"
                    empty="No sessions were scheduled in this period."
                  />
                </div>
              )}

              {/* Every row is one trainer, course or lab, so the row opens
                  it through Waypoint -- the same record the breakdown
                  popups above open. Every cell wraps its text in a span,
                  so the default ['TR','TD'] would leave only the padding
                  around the text clickable; "*" makes the whole row the
                  target. Nothing inside a row handles its own click. */}
              <div className="td-list td-list--report">
                <NewCustomDataTable
                  data={report.rows}
                  headers={headers}
                  totalRecords={report.rows.length}
                  showToolbar={false}
                  showPagination={false}
                  showFooter={false}
                  onRowClick={openRow}
                  validRowClickTagNames="*"
                  rowProps={(row) => {
                    const target = rowTarget(row);
                    if (!target) return {};
                    return {
                      tabIndex: 0,
                      role: "button",
                      "aria-label": `Open ${target[2] || "this record"}`,
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openEntity(target[0], target[1]);
                        }
                      },
                    };
                  }}
                />
              </div>
            </>
          )}
        </>
      )}

      {breakdown && (
        <ReportBreakdownModal
          breakdown={breakdown}
          onClose={closeBreakdown}
          onOpenEntity={bridgeFromBreakdown}
        />
      )}
    </div>
  );
};

export default ReportsTab;
